import { Box, Table, Tag, TagLabel, TagLeftIcon, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailPageTask } from '../api/types';
import { dateTimeTransformer } from '../helpers/dateTransformer';
import { getPriorityMap } from '../helpers/general';
import { ContentCard } from '../layout/ContentCard';
import { StatusTag } from './StatusTag';

type Props = {
  tasks: DetailPageTask[] | undefined;
  isLoading?: boolean;
  m?: ComponentProps<typeof ContentCard>['m'];
};

export const TasksTableCard = ({ tasks = [], isLoading, m = 'inherit' }: Props) => {
  const { t } = useTranslation();
  const PRIORITY_MAP = getPriorityMap(t);

  const sortedTasks = tasks.toSorted(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const getTaskTypeLabel = (type: string) => {
    const key = `tasks:data_table.types.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type;
  };

  const getPriorityDisplay = (priority: number) => {
    const priorityObj = PRIORITY_MAP.find((item) => item.value === priority);
    if (!priorityObj) return <Text>-</Text>;
    return (
      <Tag size="sm" variant="outline">
        <TagLeftIcon boxSize="14px" as={() => priorityObj.icon} />
        <TagLabel ml={1}>{priorityObj.text}</TagLabel>
      </Tag>
    );
  };

  return (
    <ContentCard header={t('tasks:detail_card.title')} isLoading={isLoading} m={m}>
      {sortedTasks.length === 0 && !isLoading ? (
        <Text color="muted" fontSize="sm">
          {t('tasks:detail_card.no_tasks')}
        </Text>
      ) : (
        <Box minW={0} overflowX="auto">
          <Table size="sm" variant="simple" minW="640px">
            <Thead>
              <Tr>
                <Th>{t('tasks:data_table.task_type')}</Th>
                <Th>{t('tasks:detail_card.status')}</Th>
                <Th>{t('tasks:task_priority.label')}</Th>
                <Th>{t('tasks:detail_card.assignee')}</Th>
                <Th>{t('tasks:detail_card.created_at')}</Th>
                <Th>{t('tasks:detail_card.completed_at')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedTasks.map((task, index) => (
                <Tr key={`${task.created_at}-${task.type}-${index}`}>
                  <Td whiteSpace="nowrap">
                    <Tag variant="primary" size="sm">
                      {getTaskTypeLabel(task.type)}
                    </Tag>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <StatusTag resourceKey="task" currentStatus={task.status} isCell />
                  </Td>
                  <Td whiteSpace="nowrap">{getPriorityDisplay(task.priority)}</Td>
                  <Td whiteSpace="nowrap">
                    <Text fontSize="sm">{task.assignee ?? '-'}</Text>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Text fontSize="sm">{dateTimeTransformer(task.created_at) ?? '-'}</Text>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Text fontSize="sm">
                      {task.completed_at ? dateTimeTransformer(task.completed_at) : '-'}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </ContentCard>
  );
};
